import { Stack } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  ACCOUNT_CREATION_SUCCESSFUL_TOAST,
  BASE_ROUTE,
  CONSUMER_ACCOUNT_CREATION,
  ENABLE_ACCOUNT,
  FORGOT_PASSWORD,
  LOGIN_ROUTE,
  LOG_IN,
  MODE_ALL,
  PROVIDER_ACCOUNT_CREATION,
  REGISTER_ACTION,
  SECURITY_PREFERENCES_ROUTE,
} from "../../Constants/CommonConstants";
import {
  SLICE_ACCOUNT_CREATION,
  SLICE_PROVIDER_ACCOUNT_CREATION,
  THUNK_POST_CREATE_ACCOUNT,
  THUNK_POST_PROVIDER_CREATE_ACCOUNT,
} from "../../Constants/SliceAndThunkConstants";
import {
  ACCOUNT_CREATED,
  ADD_OR_EDIT_VERIFICATION,
  INVALID_OTP,
  MANUAL,
  OTP_INVALID_ERROR,
  USER_ACTION_VERIFY,
  VERIFY_ACCOUNT_FORM,
  VERIFY_ACCOUNT_OTP_FORM,
} from "../../Constants/VerifyAccountConstants";
import {
  clearAccountCreationApiResponse,
  postValidateOTP,
  resetValidateOTPDescription,
} from "../../Features/AccountCreationSlice";
import {
  postLoginValidateOTP,
  postRegisterEditVerificationMethod,
  resetLoginValidateOTPDescription,
} from "../../Features/LoginSlice";
import {
  clearProviderAccountCreationApiResponse,
  postProviderValidateOTP,
  resetProviderValidateOTPDescription,
} from "../../Features/ProviderAccountCreationSlice";
import { setDisplayToast } from "../../Features/UtilityDataSlice";
import { manageApiResponseSelector } from "../../Selectors/ApiResponseSelector";
import { manageVerificationSelector } from "../../Selectors/VerificationSelector";
import { ResendOTP } from "./ResendOTP";
import { VerifyAccountButton } from "./VerifyAccountButton";
import { VerifyAccountOTPFieldWrapper } from "./VerifyAccountOTPFieldWrapper";

/**
 * @description Displays the Verify Account OTP Input Form
 * @param {string} previousScreen specifies the previous screen for verify account
 * @param {string} selectedVerificationMethod specifies the selected verification method
 * @returns {ReactNode} Renders the OTP Input form to the user
 */
export const VerifyAccountForm = ({
  previousScreen,
  selectedVerificationMethod,
}) => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { validateOTPDescription, authToken } = useSelector((state) =>
    manageVerificationSelector(state, previousScreen)
  );

  const methods = useForm({
    mode: MODE_ALL,
  });

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_ACCOUNT_CREATION,
      THUNK_POST_CREATE_ACCOUNT
    )
  );
  const { isSuccess, description } = apiResponses;

  const createValidateOtpPayload = (data) => {
    const payload = {
      authToken: authToken,
      userAction: USER_ACTION_VERIFY,
      otp: data.otp,
    };
    return payload;
  };

  const { apiResponses: providerApiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_PROVIDER_ACCOUNT_CREATION,
      THUNK_POST_PROVIDER_CREATE_ACCOUNT
    )
  );
  const {
    isSuccess: providerCreationIsSuccess,
    description: providerCreationDescription,
  } = providerApiResponses;

  useEffect(() => {
    if (validateOTPDescription === INVALID_OTP) {
      methods.setError(VERIFY_ACCOUNT_OTP_FORM, {
        type: MANUAL,
        message: OTP_INVALID_ERROR,
      });
      dispatch(resetValidateOTPDescription());
      dispatch(resetLoginValidateOTPDescription());
      dispatch(resetProviderValidateOTPDescription());
    }
  }, [validateOTPDescription, methods, dispatch]);

  useEffect(() => {
    if (
      (isSuccess && description === ACCOUNT_CREATED) ||
      (providerCreationIsSuccess &&
        providerCreationDescription === ACCOUNT_CREATED)
    ) {
      navigate(`${BASE_ROUTE}${LOGIN_ROUTE}`);
      dispatch(clearAccountCreationApiResponse(THUNK_POST_CREATE_ACCOUNT));
      dispatch(
        clearProviderAccountCreationApiResponse(
          THUNK_POST_PROVIDER_CREATE_ACCOUNT
        )
      );
      dispatch(setDisplayToast(ACCOUNT_CREATION_SUCCESSFUL_TOAST));
    }
  }, [
    dispatch,
    isSuccess,
    navigate,
    description,
    providerCreationIsSuccess,
    providerCreationDescription,
  ]);
  const handleVerifyAccount = (data) => {
    if (
      previousScreen === LOG_IN ||
      previousScreen === FORGOT_PASSWORD ||
      previousScreen === ENABLE_ACCOUNT
    ) {
      const payload = {
        authToken: authToken,
        otpType: selectedVerificationMethod,
        otp: data.otp,
      };
      dispatch(postLoginValidateOTP(payload));
    }
    if (previousScreen === CONSUMER_ACCOUNT_CREATION) {
      const payload = createValidateOtpPayload(data);
      dispatch(postValidateOTP(payload));
    }
    if (previousScreen === PROVIDER_ACCOUNT_CREATION) {
      const payload = {
        authToken: authToken,
        userAction: USER_ACTION_VERIFY,
        otp: data.otp,
      };
      dispatch(postProviderValidateOTP(payload));
    }
    if (
      previousScreen === ADD_OR_EDIT_VERIFICATION ||
      previousScreen === SECURITY_PREFERENCES_ROUTE
    ) {
      const payload = {
        otp: data.otp,
        authToken: authToken,
        userAction: USER_ACTION_VERIFY,
        otpType: selectedVerificationMethod,
      };
      dispatch(
        postRegisterEditVerificationMethod({
          data: payload,
          apiAction: REGISTER_ACTION,
        })
      );
    }
  };

  return (
    <Stack spacing={{ xs: 3, md: 5 }}>
      <FormProvider {...methods}>
        <form
          id={VERIFY_ACCOUNT_FORM}
          onSubmit={methods.handleSubmit(handleVerifyAccount)}
        >
          <VerifyAccountOTPFieldWrapper
            previousScreen={previousScreen}
            selectedVerificationMethod={selectedVerificationMethod}
          />
        </form>
      </FormProvider>
      <Stack spacing={{ xs: 3, md: 8 }}>
        <ResendOTP
          previousScreen={previousScreen}
          selectedVerificationMethod={selectedVerificationMethod}
        />
        <VerifyAccountButton />
      </Stack>
    </Stack>
  );
};

VerifyAccountForm.propTypes = {
  /**
   * specifies the previous screen
   */
  previousScreen: PropTypes.string.isRequired,
  /**
   * specifies the selected verification method
   */
  selectedVerificationMethod: PropTypes.string.isRequired,
};
